/**
 * CompleteProfileModal - Modal para completar perfil antes de ver clips
 * 
 * Se muestra cuando un jugador quiere ver/reproducir un clip pero no ha completado:
 * - Nombre
 * - Apellido  
 * - Contraseña
 */
import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { User, Lock, Eye, EyeOff, Loader2, Play, X } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { authAPI } from '../services/api';
import { useAuthStore } from '../store';
import { toast } from 'sonner';

export default function CompleteProfileModal({ 
  isOpen, 
  onClose, 
  onComplete,
  clipToPlay = null 
}) {
  const { user, setUser } = useAuthStore();
  
  const [firstName, setFirstName] = useState(user?.first_name || '');
  const [lastName, setLastName] = useState(user?.last_name || '');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e?.preventDefault();

    // Validations
    if (!firstName.trim()) {
      toast.error('Ingresa tu nombre');
      return;
    }
    if (!lastName.trim()) {
      toast.error('Ingresa tu apellido');
      return;
    }
    if (password.length < 8) {
      toast.error('La contraseña debe tener al menos 8 caracteres');
      return;
    }
    if (password !== confirmPassword) {
      toast.error('Las contraseñas no coinciden');
      return;
    }

    setLoading(true);
    try {
      const response = await authAPI.completePlayerProfile({
        user_id: user.id,
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        password: password,
        confirm_password: confirmPassword
      });

      // Update user in store
      setUser(response.data.user);
      
      toast.success('¡Perfil completado! Ya puedes ver tus replays');
      onComplete?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar perfil');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div 
        initial={{ opacity: 0 }} 
        animate={{ opacity: 1 }} 
        exit={{ opacity: 0 }} 
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 p-4"
        onClick={(e) => e.target === e.currentTarget && onClose()}
      >
        <motion.div 
          initial={{ scale: 0.95, opacity: 0, y: 20 }} 
          animate={{ scale: 1, opacity: 1, y: 0 }} 
          exit={{ scale: 0.95, opacity: 0, y: 20 }}
          className="w-full max-w-md rounded-2xl bg-[#0D121D] border border-white/10 overflow-hidden shadow-2xl"
          data-testid="complete-profile-modal"
        >
          {/* Header - Gold Standard */}
          <div className="bg-gradient-to-br from-[#131B2A] to-[#0D121D] p-6 text-white border-b border-white/10">
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <Play className="h-6 w-6 text-[#BBFF00]" />
                  <p className="text-[#BBFF00] text-sm font-mono tracking-wider uppercase">
                    Un paso más
                  </p>
                </div>
                <h2 className="text-2xl font-display font-bold mt-2 text-slate-50">
                  Completa tu perfil para ver clips
                </h2>
              </div>
              <button 
                onClick={onClose}
                className="p-1.5 rounded-full hover:bg-white/10 transition-colors text-slate-400 hover:text-white"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-4 bg-[#0D121D]">
            {/* Current phone display */}
            <div className="bg-[#131B2A] rounded-lg p-3 flex items-center gap-2 border border-white/10">
              <div className="bg-[#BBFF00]/20 p-2 rounded-full">
                <User className="h-4 w-4 text-[#BBFF00]" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Celular verificado</p>
                <p className="font-medium text-slate-200">{user?.id || user?.phone}</p>
              </div>
            </div>

            {/* Name fields */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="firstName" className="text-slate-300">Nombre</Label>
                <Input
                  id="firstName"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="Tu nombre"
                  className="h-11 bg-[#131B2A] border-white/10 text-slate-100 placeholder:text-slate-500 focus:border-[#BBFF00] focus:ring-[#BBFF00]/20"
                  data-testid="first-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName" className="text-slate-300">Apellido</Label>
                <Input
                  id="lastName"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Tu apellido"
                  className="h-11 bg-[#131B2A] border-white/10 text-slate-100 placeholder:text-slate-500 focus:border-[#BBFF00] focus:ring-[#BBFF00]/20"
                  data-testid="last-name-input"
                />
              </div>
            </div>

            {/* Password fields */}
            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-300">Crea una contraseña</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Mínimo 8 caracteres"
                  className="h-11 pr-10 bg-[#131B2A] border-white/10 text-slate-100 placeholder:text-slate-500 focus:border-[#BBFF00] focus:ring-[#BBFF00]/20"
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-slate-300">Confirma contraseña</Label>
              <Input
                id="confirmPassword"
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Repite tu contraseña"
                className="h-11 bg-[#131B2A] border-white/10 text-slate-100 placeholder:text-slate-500 focus:border-[#BBFF00] focus:ring-[#BBFF00]/20"
                data-testid="confirm-password-input"
              />
            </div>

            {/* Info box */}
            <div className="bg-amber-500/10 rounded-xl p-4 border border-amber-500/30">
              <p className="text-sm text-amber-200">
                <strong>💡 ¿Por qué necesitamos esto?</strong>
                <br />
                Para que puedas iniciar sesión desde cualquier dispositivo y ver tus clips.
              </p>
            </div>

            {/* Submit button */}
            <Button 
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-[#BBFF00] hover:bg-[#A2DC00] text-[#070A0F] font-bold shadow-[0_0_20px_rgba(187,255,0,0.3)] transition-all"
              data-testid="complete-profile-button"
            >
              {loading ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <>
                  <Play className="h-5 w-5 mr-2" />
                  Guardar y ver mi clip
                </>
              )}
            </Button>

            <button 
              type="button"
              onClick={onClose}
              className="w-full text-sm text-slate-500 hover:text-slate-300 py-2 transition-colors"
            >
              Ahora no, después
            </button>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
